var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import './PlaintextEditor.scss';
import classNames from 'classnames';
import { COPY_GENERIC } from '@sonar-software/sonar-ts-constants';
import { EditBox } from '@ui/EditBox';
import { haltClickEventPropagation } from '@lib/events';
import { OutsideClickWrapper } from '@ui/general';
import { Button, Input, Tooltip } from 'antd';
import { useCurrentUser } from '@hooks';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { validateText } from '@lib/validation';
function hasNewline(value) {
    return (/\r|\n/u).test(value);
}
export const PlaintextEditor = forwardRef(({ allowNewline, canBeEmpty, className, hideButtons, id, isDisabled, isProcessing, isReadOnly, isSalesforce, isSpreadsheet, maxLength, maxRows, onBlur, onCancel, onChange, onFocus, onSave, onStartEditing, placeholder, processingResult, rules, saveOnBlur, saveOnEnter, saveOnTab, showProcessing, skipSpellcheck, tooltip, value }, ref) => {
    var _a, _b;
    const { isReadOnly: isUserReadOnly } = useCurrentUser();
    const [state, setState] = useState({
        editingValue: value !== null && value !== void 0 ? value : '',
        isEditing: false,
        isProcessing: false,
        showValidation: false,
        validationMessage: ''
    });
    const valueBackupRef = useRef(value !== null && value !== void 0 ? value : '');
    const textRef = useRef(null);
    const isEditable = !isReadOnly && !isDisabled && !isUserReadOnly;
    const handleSave = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        e === null || e === void 0 ? void 0 : e.preventDefault();
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        if (state.isEditing) {
            setState((current) => (Object.assign(Object.assign({}, current), { isEditing: false, isProcessing: true, showValidation: false })));
            const result = onSave(state.editingValue);
            if (result === null || result === void 0 ? void 0 : result.then) {
                yield result;
            }
            setState((current) => (Object.assign(Object.assign({}, current), { isEditing: false, isProcessing: false })));
            (_a = textRef.current) === null || _a === void 0 ? void 0 : _a.blur();
            if (onBlur) {
                onBlur();
            }
        }
    }), [onBlur, onSave, state.editingValue, state.isEditing]);
    useEffect(() => {
        if (value !== state.editingValue &&
            !state.isEditing &&
            !state.isProcessing &&
            !state.showValidation &&
            (!processingResult || processingResult.success)) {
            setState((current) => (Object.assign(Object.assign({}, current), { editingValue: value !== null && value !== void 0 ? value : '' })));
        }
    }, [processingResult, state.editingValue, state.isEditing, state.isProcessing, state.showValidation, value]);
    const handleOnChange = (e) => {
        const currentValue = e.target.value;
        const message = validateText(currentValue, rules);
        setState((current) => (Object.assign(Object.assign({}, current), { editingValue: currentValue, showValidation: Boolean(message), validationMessage: message !== null && message !== void 0 ? message : current.validationMessage })));
        if (onChange) {
            onChange(currentValue);
        }
    };
    const handleCancel = useCallback((e) => {
        var _a;
        e === null || e === void 0 ? void 0 : e.preventDefault();
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        let targetValue = valueBackupRef.current;
        if (state.showValidation) {
            targetValue = state.editingValue;
        }
        setState((current) => (Object.assign(Object.assign({}, current), { editingValue: targetValue, isEditing: false, showValidation: current.showValidation })));
        (_a = textRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        if (onCancel) {
            onCancel();
        }
    }, [onCancel, state.editingValue, state.showValidation]);
    const handleClick = useCallback((e) => {
        e === null || e === void 0 ? void 0 : e.preventDefault();
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        if (onFocus) {
            onFocus();
        }
        if (!state.isEditing) {
            valueBackupRef.current = state.editingValue;
            setState((current) => (Object.assign(Object.assign({}, current), { isEditing: true, isProcessing: false })));
            if (onStartEditing) {
                onStartEditing();
            }
        }
        setTimeout(() => { var _a; return (_a = textRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 50);
    }, [onFocus, onStartEditing, state.editingValue, state.isEditing]);
    const handleClickOutside = useCallback(() => {
        if (state.isEditing) {
            if (saveOnBlur && !state.showValidation) {
                handleSave();
            }
            else {
                handleCancel();
            }
        }
    }, [handleCancel, handleSave, saveOnBlur, state.isEditing, state.showValidation]);
    const handleKeyUp = (e) => {
        if (e.code === 'Escape') {
            handleCancel();
        }
    };
    const handleEnter = (e) => {
        if (saveOnEnter && e.code === 'Enter' && !e.shiftKey && !state.showValidation) {
            handleSave();
        }
        if (!allowNewline) {
            e.preventDefault();
            e.stopPropagation();
        }
    };
    const handleKeyDown = (e) => {
        if (e.code === 'Tab') {
            if (saveOnTab) {
                if (!state.showValidation) {
                    setTimeout(() => handleSave(), 25);
                }
            }
            else {
                e.preventDefault();
                e.stopPropagation();
            }
        }
    };
    useImperativeHandle(ref, () => ({
        edit: handleClick,
        save: () => onSave(value !== null && value !== void 0 ? value : '')
    }));
    const validationProcessingResult = {
        message: state.isEditing ? undefined : state.validationMessage,
        success: false
    };
    const processingState = {
        isProcessing: isProcessing !== null && isProcessing !== void 0 ? isProcessing : false,
        result: state.showValidation ? validationProcessingResult : processingResult
    };
    const isSingleLine = !allowNewline ||
        (!hasNewline(state.editingValue) && ((_b = (_a = textRef.current) === null || _a === void 0 ? void 0 : _a.resizableTextArea) === null || _b === void 0 ? void 0 : _b.textArea.rows) === 1);
    return (React.createElement(Tooltip, { placement: 'bottom', title: tooltip, trigger: 'hover' },
        React.createElement(Tooltip, { open: state.isEditing && state.showValidation, placement: 'bottom', title: state.validationMessage },
            React.createElement(OutsideClickWrapper, { className: classNames('plaintext-editor', {
                    editing: state.isEditing,
                    readonly: isReadOnly || isUserReadOnly,
                    'single-line': isSingleLine
                }, className), id: id, onClick: isEditable ? handleClick : haltClickEventPropagation, onClickOutside: isEditable ? handleClickOutside : undefined },
                React.createElement(EditBox, { editIcon: true, isReadOnly: isReadOnly || isUserReadOnly, isSalesforce: isSalesforce, isSpreadsheet: isSpreadsheet, state: showProcessing || state.showValidation ? processingState : undefined },
                    React.createElement("div", { className: 'plaintext-editor-wrapper' },
                        React.createElement(Input.TextArea, { autoSize: {
                                maxRows,
                                minRows: 1
                            }, disabled: !isEditable, maxLength: maxLength, onChange: handleOnChange, onFocus: handleClick, onKeyDown: handleKeyDown, onKeyUp: handleKeyUp, onPressEnter: handleEnter, placeholder: placeholder, ref: textRef, spellCheck: !skipSpellcheck, value: state.editingValue, variant: 'borderless' })),
                    state.isEditing && !hideButtons &&
                        React.createElement("div", null,
                            React.createElement(Button, { className: 'action', disabled: !canBeEmpty && !state.editingValue, onClick: handleSave, type: 'primary' }, COPY_GENERIC.SAVE),
                            React.createElement(Button, { className: 'action', onClick: handleCancel }, COPY_GENERIC.CANCEL)))))));
});
